import React, { useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Box, Heading, Flex, Button, SimpleGrid, Spinner, Tag, Badge, HStack, Text,
  Input, Select, useToast, useDisclosure, Modal, ModalOverlay, ModalContent,
  ModalHeader, ModalBody, ModalFooter, ModalCloseButton, FormControl, FormLabel,
  Textarea, AvatarGroup, Avatar, Progress,
} from "@chakra-ui/react";
import { AddIcon } from "@chakra-ui/icons";
import { api, logout } from "../../lib/api";
import { useShellHeader } from "../../Components/AppShell";

const STATUS_COLORS = {
  planning: "gray",
  active: "blue",
  on_hold: "orange",
  completed: "green",
  archived: "purple",
};

export default function ProjectsList() {
  const navigate = useNavigate();
  const toast = useToast();
  const [projects, setProjects] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const create = useDisclosure();
  const empty = { name: "", description: "", color: "#2D5BFF", status: "planning", team: "", dueDate: "" };
  const [f, setF] = useState(empty);

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (status) params.set("status", status);
      const [p, t] = await Promise.all([
        api(`/projects?${params.toString()}`),
        api("/admin/teams").catch(() => ({ teams: [] })),
      ]);
      setProjects(p.projects || []);
      setTeams(t.teams || []);
    } catch (e) {
      if (e.status === 401) { logout(); navigate("/login"); return; }
      toast({ title: e.message, status: "error" });
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function createProject() {
    try {
      const body = {
        name: f.name,
        description: f.description,
        color: f.color,
        status: f.status,
        team: f.team || null,
        dueDate: f.dueDate || null,
      };
      const r = await api("/projects", { method: "POST", body: JSON.stringify(body) });
      toast({ title: "Project created", status: "success" });
      create.onClose();
      setF(empty);
      navigate(`/projects/${r.project._id}`);
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }

  useShellHeader({
    title: "Projects",
    actions: <Button leftIcon={<AddIcon />} colorScheme="blue" onClick={create.onOpen}>New Project</Button>,
  }, [create]);

  return (
    <Box>
      <Flex mb={6} align="center" gap={3} wrap="wrap">
        <Input placeholder="Search projects" value={q} onChange={(e) => setQ(e.target.value)} maxW="240px" onKeyDown={(e) => e.key === "Enter" && load()} bg="white" />
        <Select placeholder="All statuses" value={status} onChange={(e) => setStatus(e.target.value)} maxW="180px" bg="white">
          {Object.keys(STATUS_COLORS).map((s) => <option key={s} value={s}>{s}</option>)}
        </Select>
        <Button onClick={load}>Apply</Button>
      </Flex>

      {loading ? <Spinner /> : (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
          {projects.map((p) => (
            <RouterLink key={p._id} to={`/projects/${p._id}`}>
              <Box p={5} bg="white" rounded="md" shadow="sm" borderTop="4px solid" borderColor={p.color} _hover={{ shadow: "md" }} transition="all 0.15s">
                <HStack justify="space-between" align="start">
                  <Box flex="1">
                    <Heading size="sm" noOfLines={1}>{p.name}</Heading>
                    <HStack mt={1} spacing={2}>
                      <Tag fontSize="xs">{p.key}</Tag>
                      <Badge colorScheme={STATUS_COLORS[p.status] || "gray"}>{p.status.replace("_", " ")}</Badge>
                    </HStack>
                  </Box>
                </HStack>
                <Text mt={3} fontSize="sm" color="gray.600" noOfLines={2}>{p.description || "—"}</Text>
                <Box mt={4}>
                  <HStack justify="space-between" mb={1}>
                    <Text fontSize="xs" color="gray.500">{p.doneCount} / {p.taskCount} tasks</Text>
                    <Text fontSize="xs" color="gray.500">{p.progress}%</Text>
                  </HStack>
                  <Progress value={p.progress} size="xs" colorScheme={p.progress === 100 ? "green" : "blue"} rounded="full" />
                </Box>
                <HStack mt={4} justify="space-between">
                  <HStack fontSize="xs" color="gray.500" spacing={2}>
                    {p.team && <Tag size="sm" bg={p.team.color || "gray.300"} color="white">{p.team.name}</Tag>}
                    {p.dueDate && <Text>Due {new Date(p.dueDate).toLocaleDateString()}</Text>}
                  </HStack>
                  <AvatarGroup size="xs" max={3}>
                    {(p.members || []).slice(0, 3).map((m) => <Avatar key={m._id || m} name={m.username || ""} />)}
                  </AvatarGroup>
                </HStack>
              </Box>
            </RouterLink>
          ))}
          {projects.length === 0 && <Box gridColumn="1/-1" textAlign="center" color="gray.500" py={12}>No projects yet — click "New Project" to start.</Box>}
        </SimpleGrid>
      )}

      <Modal isOpen={create.isOpen} onClose={create.onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>New Project</ModalHeader><ModalCloseButton />
          <ModalBody>
            <SimpleGrid columns={2} spacing={3}>
              <FormControl gridColumn="1/-1"><FormLabel>Name</FormLabel><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></FormControl>
              <FormControl gridColumn="1/-1"><FormLabel>Description</FormLabel><Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></FormControl>
              <FormControl><FormLabel>Status</FormLabel>
                <Select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
                  {Object.keys(STATUS_COLORS).map((s) => <option key={s} value={s}>{s}</option>)}
                </Select>
              </FormControl>
              <FormControl><FormLabel>Color</FormLabel><Input type="color" value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })} /></FormControl>
              <FormControl><FormLabel>Team</FormLabel>
                <Select placeholder="Auto (mine)" value={f.team} onChange={(e) => setF({ ...f, team: e.target.value })}>
                  {teams.map((t) => <option key={t._id} value={t._id}>{t.name}</option>)}
                </Select>
              </FormControl>
              <FormControl><FormLabel>Due date</FormLabel><Input type="date" value={f.dueDate} onChange={(e) => setF({ ...f, dueDate: e.target.value })} /></FormControl>
            </SimpleGrid>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={create.onClose}>Cancel</Button>
            <Button colorScheme="blue" onClick={createProject} isDisabled={!f.name}>Create</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
